import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import {
  BRANDING_TOKENS,
  TOKEN_GROUP_LABELS,
  isHexColor,
  overriddenKeys,
  resolveOrgBranding,
  validateContrast,
  type Branding,
  type BrandingOverrides,
  type ContrastIssue,
  type ThemePreset,
  type TokenDef,
  type TokenGroup,
} from "@gatiq/shared";
import type { SessionUser } from "@gatiq/shared";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Skeleton,
  useToast,
} from "@/ui";
import { TabBarPortal } from "@/layout/PageChrome";
import { ContrastWarnings } from "./ContrastWarnings";
import { BrandPreview } from "./BrandPreview";
import { ProfilePhotoField } from "./ProfilePhotoField";
import { LogoCropDialog, type PendingPick } from "./LogoCropDialog";

interface BrandingApiResponse {
  platform: Branding;
  overrides: BrandingOverrides;
  effective: Branding;
  tokens: readonly TokenDef[];
  presets: readonly ThemePreset[];
  issues: ContrastIssue[];
}

/** One sentence of guidance shown above the active group's fields. */
const GROUP_GUIDANCE: Record<TokenGroup, string> = {
  identity: "The name and marks shown on the sign-in screen, the sidebar and every email.",
  brand: "The two core colors — five more shades are derived from Color 1 automatically.",
  semantic: "Status colors for success, warning, error and info states — never the brand accent.",
  typography: "Fonts and text sizing for headings and body copy.",
  shape: "Corner radii, shadow depth and border style used across every card and control.",
  layout: "Sidebar, header, spacing and content width for the app shell.",
  contact: "Support details shown in the footer and in emails.",
};

export interface BrandingEditorProps {
  mode: "platform" | "org";
  /** API path, no leading `/api`, e.g. "/org/branding" or "/org/branding/platform". */
  basePath: string;
  /**
   * Which field group to show. Driven by the URL, so every group is its own
   * tab and its own link — see `SettingsLayout`. One instance of this editor
   * stays mounted across all of them, which is what lets an unsaved draft
   * survive a tab change.
   */
  activeGroup: TokenGroup;
}

/**
 * The Branding tab of Settings.
 *
 * This renders a PANEL, not a page: `SettingsLayout` owns the `<Page>`, the
 * "Settings" heading and the tab row. Rendering its own `<PageHeader>` here
 * would put a second heading directly under the first.
 */
export function BrandingEditor({ mode, basePath, activeGroup }: BrandingEditorProps) {
  const toast = useToast();
  const { setUser } = useAuth();
  const [data, setData] = useState<BrandingApiResponse | null>(null);
  /*
   * The avatar rides this form's Save but is not a branding token, so it gets
   * its own draft slot: `undefined` untouched, a data URL staged, `null` a
   * staged removal. Keeping it out of `pending` stops it being PATCHed to the
   * branding endpoint, which would reject it as an unknown key.
   */
  const [pendingPhoto, setPendingPhoto] = useState<string | null | undefined>(undefined);
  const [pending, setPending] = useState<BrandingOverrides>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /** A logo picked but not yet cropped: the file plus a throwaway preview URL. */
  const [pick, setPick] = useState<(PendingPick & { key: string }) | null>(null);
  // The ref mirrors `pick` synchronously: `handleCrop` reads it while the state
  // update from `setPick` is still queued, so it cannot trust the closure copy.
  const pickRef = useRef<(PendingPick & { key: string }) | null>(null);

  const stagePick = useCallback((key: string, file: File) => {
    const next = { key, file, url: URL.createObjectURL(file) };
    pickRef.current = next;
    setPick(next);
  }, []);

  /** The blob URL exists only so the crop dialog can preview the image. */
  const closePick = useCallback(() => {
    const prev = pickRef.current;
    pickRef.current = null;
    if (prev) URL.revokeObjectURL(prev.url);
    setPick(null);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api
      .get<BrandingApiResponse>(basePath)
      .then((res) => {
        setData(res);
        setPending(res.overrides);
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : "Could not load branding.");
      })
      .finally(() => setLoading(false));
  }, [basePath]);

  useEffect(() => {
    load();
  }, [load]);

  const effective = useMemo(
    () => (data ? resolveOrgBranding(data.platform, pending) : null),
    [data, pending],
  );
  const issues = useMemo(() => (effective ? validateContrast(effective) : []), [effective]);
  const overridden = useMemo(() => new Set(overriddenKeys(pending)), [pending]);

  /** Diff of the draft against what is actually saved — drives the "N unsaved" indicator. */
  const unsavedCount = useMemo(() => {
    if (!data) return 0;
    const keys = new Set<string>([...Object.keys(data.overrides), ...Object.keys(pending)]);
    let count = 0;
    for (const key of keys) {
      if ((data.overrides[key] ?? null) !== (pending[key] ?? null)) count += 1;
    }
    if (pendingPhoto !== undefined) count += 1;
    return count;
  }, [data, pending, pendingPhoto]);

  const canSave = issues.length === 0 && !saving && Boolean(data) && unsavedCount > 0;

  // Guardrail: warn before the tab closes with unsaved work, and stop warning
  // the moment the draft matches what is saved.
  useEffect(() => {
    if (unsavedCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [unsavedCount]);

  const setField = useCallback((key: string, value: string) => {
    setPending((prev) => ({ ...prev, [key]: value === "" ? null : value }));
  }, []);

  const scheduleReload = () => {
    window.setTimeout(() => window.location.reload(), 700);
  };

  const resetField = useCallback(
    async (key: string, label: string) => {
      setBusyKey(key);
      try {
        const res =
          mode === "org"
            ? await api.post<BrandingApiResponse>(`${basePath}/reset`, { keys: [key] })
            : await api.patch<BrandingApiResponse>(basePath, { branding: { [key]: null } });
        setData(res);
        setPending((prev) => ({ ...prev, [key]: null }));
        toast.success(`${label} reset to default.`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not reset that field.");
      } finally {
        setBusyKey(null);
      }
    },
    [basePath, mode, toast],
  );

  const resetAll = useCallback(async () => {
    setSaving(true);
    try {
      const res =
        mode === "org"
          ? await api.post<BrandingApiResponse>(`${basePath}/reset`, {})
          : await api.patch<BrandingApiResponse>(basePath, {
              branding: Object.fromEntries(BRANDING_TOKENS.map((t) => [t.key, null])),
            });
      setData(res);
      setPending(res.overrides);
      toast.success("Reset to the GatiQ default theme.");
      scheduleReload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not reset branding.");
    } finally {
      setSaving(false);
    }
  }, [basePath, mode, toast]);

  const applyPreset = useCallback(
    async (preset: ThemePreset) => {
      setSaving(true);
      try {
        const res = await api.post<BrandingApiResponse>(`${basePath}/preset`, { presetId: preset.id });
        setData(res);
        setPending(res.overrides);
        toast.success(`${preset.label} theme applied.`);
        scheduleReload();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not apply that preset.");
      } finally {
        setSaving(false);
      }
    },
    [basePath, toast],
  );

  const handleUpload = useCallback(
    async (key: string, dataUrl: string, aspect: "square" | "landscape") => {
      setBusyKey(key);
      try {
        const res = await api.post<{ url: string; overrides: BrandingOverrides }>(`${basePath}/upload`, {
          field: key,
          dataUrl,
          aspect,
        });
        setPending((prev) => ({ ...prev, [key]: res.url }));
        toast.success("Image uploaded.");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not upload that image.");
      } finally {
        setBusyKey(null);
      }
    },
    [basePath, toast],
  );

  const handleCrop = useCallback(
    (ratio: number, dataUrl: string) => {
      const key = pickRef.current?.key;
      closePick();
      if (key) void handleUpload(key, dataUrl, ratio === 1 ? "square" : "landscape");
    },
    [closePick, handleUpload],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      /* The avatar first, and through its own endpoint. It is written before
       * the branding PATCH so that a contrast rejection there cannot leave the
       * photo silently unsaved while the toast reports failure. */
      if (pendingPhoto !== undefined) {
        const me = pendingPhoto
          ? await api.post<SessionUser>("/auth/me/photo", { dataUrl: pendingPhoto })
          : await api.del<SessionUser>("/auth/me/photo");
        // The header reads the avatar off the session user, so putting the
        // response back into the auth context is what repaints it.
        setUser(me);
        setPendingPhoto(undefined);
      }

      const res = await api.patch<BrandingApiResponse>(basePath, { branding: pending });
      setData(res);
      setPending(res.overrides);
      toast.success("Branding saved.");
      scheduleReload();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "CONTRAST_TOO_LOW") {
        toast.error("Fix the highlighted contrast issues before saving.");
      } else {
        toast.error(err instanceof Error ? err.message : "Could not save branding.");
      }
    } finally {
      setSaving(false);
    }
  }, [basePath, pending, pendingPhoto, setUser, toast]);

  const applySuggestion = useCallback((tokenKey: string, hex: string) => {
    setPending((prev) => ({ ...prev, [tokenKey]: hex }));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (loadError || !data || !effective) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <Alert kind="danger" title="Could not load branding">
          {loadError ?? "Something went wrong."}
        </Alert>
        <div>
          <Button onClick={load} variant="secondary">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const grouped = new Map<TokenGroup, TokenDef[]>();
  for (const token of data.tokens) {
    const arr = grouped.get(token.group) ?? [];
    arr.push(token);
    grouped.set(token.group, arr);
  }

  const activeTokens = grouped.get(activeGroup) ?? [];

  const saveDisabledReason = saving
    ? undefined
    : issues.length > 0
      ? "Fix the contrast issues below before saving."
      : unsavedCount === 0
        ? "There is nothing to save yet."
        : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Action row, not a heading: the page already says "Settings" and the
          tab already says "Branding". This only carries the scope and the two
          controls that act on the whole draft. */}
      {/*
       * Actions live at the end of the tab row, not in a row of their own.
       *
       * The scope sentence that used to sit here is gone: the page
       * description already states it, and a line of text that never changes
       * was costing a whole row on a screen that never scrolls.
       */}
      <TabBarPortal>
        {mode === "org" ? (
          <span className="hidden text-sm text-muted xl:inline">Presets</span>
        ) : null}
        {mode === "org"
          ? data.presets.map((preset) => (
              <button
                aria-label={`Apply the ${preset.label} preset`}
                className="size-6 shrink-0 rounded-full border border-rule transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
                key={preset.id}
                onClick={() => void applyPreset(preset)}
                style={{
                  background:
                    preset.id === "gatiq"
                      ? "linear-gradient(135deg,#C54E1B,#FAFAFA)"
                      : preset.swatch,
                }}
                title={`Apply the ${preset.label} preset`}
                type="button"
              />
            ))
          : null}

        {unsavedCount > 0 ? (
          <span className="flex items-center gap-1.5 text-sm text-muted">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-brand" />
            {unsavedCount} unsaved
          </span>
        ) : null}
        <Button disabled={saving} onClick={() => void resetAll()} size="sm" variant="secondary">
          Reset all
        </Button>
        <Button
          disabled={!canSave}
          loading={saving}
          onClick={() => void handleSave()}
          size="sm"
          title={saveDisabledReason}
        >
          Save
        </Button>
      </TabBarPortal>

      {/*
       * `items-start` + `max-h-full`, not `items-stretch`.
       *
       * Stretching made both columns the same height whatever they held, so a
       * five-field group like Identity left ~180px of empty card below its
       * last row. Sizing to content removes that; `max-h-full` still caps a
       * long group at the viewport so its body scrolls rather than the page
       * (D-15). Ragged bottoms are the honest shape of unequal content.
       */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:flex-row lg:items-start lg:overflow-hidden">
        {/* Only the active group's fields render here — this is what kills the scroll. */}
        <Card className="min-w-0 lg:max-h-full lg:flex-1" fill>
          <div className="shrink-0">
            <CardHeader description={GROUP_GUIDANCE[activeGroup]} title={TOKEN_GROUP_LABELS[activeGroup]} />
          </div>
          <CardBody className="min-h-0 flex-1 divide-y divide-rule/60 overflow-y-auto">
            {activeTokens.map((token) => (
              <TokenField
                busy={busyKey === token.key}
                effectiveValue={effective[token.key] ?? ""}
                key={token.key}
                onChange={(value) => setField(token.key, value)}
                onReset={() => void resetField(token.key, token.label)}
                onStage={(file) => stagePick(token.key, file)}
                overridden={overridden.has(token.key)}
                token={token}
                value={pending[token.key] ?? null}
              />
            ))}
            {/* Identity only: the marks on this screen are the organization's,
                and this one is the signed-in person's own. */}
            {activeGroup === "identity" ? (
              <ProfilePhotoField
                onClear={() => setPendingPhoto(null)}
                onStage={(dataUrl) => setPendingPhoto(dataUrl)}
                staged={pendingPhoto}
              />
            ) : null}
          </CardBody>
        </Card>

        {/* Preview — fixed, same height as the field card, with its own
            small header and the contrast status directly beneath it so the
            column reads as one panel rather than one floating card. */}
        <Card className="flex min-h-0 flex-col overflow-hidden lg:max-h-full lg:w-[20rem] lg:shrink-0 xl:w-[24rem]">
          <div className="shrink-0">
            <CardHeader title="Preview" />
          </div>
          {/* `m-auto` on the child, NOT `justify-center` on the box: a
              flex-centred child that outgrows its container has its top edge
              pushed above the scroll origin, where nothing can reach it. Auto
              margins centre only while there is spare room and collapse to
              normal flow once the content overflows. */}
          <CardBody className="flex min-h-0 flex-1 overflow-y-auto">
            <div className="m-auto w-full">
              <BrandPreview branding={effective} />
            </div>
          </CardBody>
          {/* Pinned to the foot of the column: the contrast verdict is the
              answer to "can I save this?", so it stays visible while the
              preview above it scrolls. */}
          <div className="shrink-0 border-t border-rule px-5 py-3.5">
            <ContrastWarnings issues={issues} onUseSuggestion={applySuggestion} />
          </div>
        </Card>
      </div>

      {/* A favicon is stored square no matter what, so hiding 16:9 here avoids
          a choice that would be discarded on save. */}
      <LogoCropDialog
        onCancel={closePick}
        onCrop={handleCrop}
        pending={pick}
        ratioLocked={pick?.key === "favicon"}
      />
    </div>
  );
}

/* ── One field, dispatched by TokenDef.type ──────────────────────── */

function TokenField({
  token,
  value,
  effectiveValue,
  overridden,
  busy,
  onChange,
  onReset,
  onStage,
}: {
  token: TokenDef;
  value: string | null;
  effectiveValue: string;
  overridden: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
  onStage: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayValue = value ?? "";

  let control: ReactNode;

  switch (token.type) {
    case "color": {
      const swatch = isHexColor(displayValue) ? displayValue : effectiveValue;
      control = (
        <div className="flex items-center gap-2">
          <input
            aria-label={`${token.label} swatch`}
            className="h-10 w-10 shrink-0 cursor-pointer rounded-md border border-rule bg-raised p-0.5"
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            type="color"
            value={swatch}
          />
          <input
            aria-label={token.label}
            className="h-10 w-full rounded-md border border-rule bg-raised px-3 text-sm text-ink placeholder:text-muted/70"
            onChange={(e) => onChange(e.target.value)}
            placeholder={effectiveValue}
            value={displayValue}
          />
        </div>
      );
      break;
    }

    case "font":
    case "enum": {
      const options = (token.options ?? []).map((o) => ({ value: o, label: o }));
      control = (
        <select
          aria-label={token.label}
          className="h-10 w-full rounded-md border border-rule bg-raised px-3 text-sm text-ink"
          onChange={(e) => onChange(e.target.value)}
          value={displayValue || effectiveValue}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    }

    case "url": {
      control = (
        <div className="flex items-center gap-3">
          {displayValue ? (
            <img alt="" className="h-10 w-10 rounded-md border border-rule bg-raised object-contain" src={displayValue} />
          ) : (
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md border border-dashed border-rule text-xs text-muted">
              —
            </div>
          )}
          <input
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onStage(file);
              e.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
          <Button
            loading={busy}
            onClick={() => fileInputRef.current?.click()}
            size="sm"
            type="button"
            variant="secondary"
          >
            Upload
          </Button>
        </div>
      );
      break;
    }

    // `link` is a typed web address, not an uploaded asset — a plain text
    // field, never the file picker that `url` gets.
    case "link":
    case "email":
    case "phone":
    case "size":
    case "text":
    default: {
      const inputType =
        token.type === "email"
          ? "email"
          : token.type === "phone"
            ? "tel"
            : token.type === "link"
              ? "url"
              : "text";
      control = (
        <input
          aria-label={token.label}
          className="h-10 w-full rounded-md border border-rule bg-raised px-3 text-sm text-ink placeholder:text-muted/70"
          onChange={(e) => onChange(e.target.value)}
          placeholder={effectiveValue}
          type={inputType}
          value={displayValue}
        />
      );
      break;
    }
  }

  return (
    <div className="grid grid-cols-1 items-start gap-x-4 gap-y-1.5 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(9rem,14rem)_1fr]">
      <label className="text-sm font-medium text-ink sm:pt-2.5">{token.label}</label>
      <div className="flex items-start gap-2">
        <div className="min-w-0 max-w-xl flex-1">
          {control}
          {token.help ? <p className="mt-1 text-xs text-muted">{token.help}</p> : null}
        </div>
        {overridden ? (
          <button
            aria-label={`Reset ${token.label}`}
            className="mt-2 shrink-0 rounded-sm p-1 text-muted transition-colors hover:bg-brand-light hover:text-brand-ink disabled:cursor-not-allowed disabled:opacity-60"
            disabled={busy}
            onClick={onReset}
            title={`Reset ${token.label} to default`}
            type="button"
          >
            <RotateCcw aria-hidden="true" className="size-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
